import { useState, useCallback } from "react";

/**
 * Hook for in-browser AI background removal using @imgly/background-removal.
 * The ONNX model (~40MB) downloads on first use and is cached by the browser.
 * Zero API cost — runs entirely client-side.
 */

interface UseBackgroundRemovalReturn {
  readonly removeBackground: (imageBlob: Blob) => Promise<Blob | null>;
  readonly isProcessing: boolean;
  readonly progress: number;
  readonly error: string | null;
}

export function useBackgroundRemoval(): UseBackgroundRemovalReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const removeBackground = useCallback(
    async (imageBlob: Blob): Promise<Blob | null> => {
      setIsProcessing(true);
      setProgress(0);
      setError(null);

      try {
        // Dynamic import — only loads the ~40MB model when needed
        const { removeBackground: removeBg } = await import(
          "@imgly/background-removal"
        );

        const result = await removeBg(imageBlob, {
          progress: (key: string, current: number, total: number) => {
            if (total > 0) {
              setProgress(Math.round((current / total) * 100));
            }
          },
          output: {
            format: "image/png",
            quality: 0.9,
          },
        });

        setProgress(100);
        return result;
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : "Erreur lors de la suppression du fond";
        setError(msg);
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  return { removeBackground, isProcessing, progress, error };
}
